import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import WalletConnect from '../WalletConnect';
import './HolderDashboard.css';

interface Asset {
  id: string;
  name: string;
  type: string;
  tokenSymbol: string;
  contractAddress?: string;
}

interface DistributionEntitlement {
  id: string;
  distributionId: string;
  amountCents: number;
  status: string;
  currency: string;
  distribution?: {
    assetId: string;
    recordDate: string;
    notes?: string;
  };
}

const HolderDashboard: React.FC = () => {
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [entitlements, setEntitlements] = useState<DistributionEntitlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [assetsData, entitlementsData] = await Promise.all([
        apiService.getAssets(),
        apiService.getDistributionEntitlements(),
      ]);
      setAssets(assetsData);
      setEntitlements(entitlementsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number, currency: string = 'NZD') => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: currency,
    }).format(cents / 100);
  };

  const totalOwed = entitlements
    .filter(e => e.status === 'OWED')
    .reduce((sum, e) => sum + e.amountCents, 0);

  const totalPaid = entitlements
    .filter(e => e.status === 'PAID')
    .reduce((sum, e) => sum + e.amountCents, 0);

  if (loading) {
    return <div className="holder-dashboard">Loading...</div>;
  }

  return (
    <div className="holder-dashboard">
      <header className="dashboard-header">
        <div>
          <h1>Shareholder Portal</h1>
          <p>Welcome, {state.user?.email}</p>
        </div>
        <div className="header-actions">
          <WalletConnect />
          <button onClick={async () => { await logout(); navigate('/', { replace: true }); }} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="dashboard-content">
        <section className="summary-section">
          <div className="summary-card">
            <h3>Total Distributions Owed</h3>
            <p className="amount">{formatCurrency(totalOwed)}</p>
          </div>
          <div className="summary-card">
            <h3>Total Distributions Paid</h3>
            <p className="amount">{formatCurrency(totalPaid)}</p>
          </div>
          <div className="summary-card">
            <h3>Assets Held</h3>
            <p className="amount">{assets.length}</p>
          </div>
        </section>

        <section className="assets-section">
          <h2>Your Holdings</h2>
          {assets.length === 0 ? (
            <p>No assets found.</p>
          ) : (
            <div className="assets-grid">
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  className="asset-card"
                  onClick={() => navigate(`/assets/${asset.id}`)}
                >
                  <h3>{asset.name}</h3>
                  <p className="asset-type">{asset.type}</p>
                  <p className="asset-symbol">{asset.tokenSymbol}</p>
                  {asset.contractAddress && (
                    <span className="contract-badge">Contract Deployed</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="distributions-section">
          <h2>Distribution History</h2>
          {entitlements.length === 0 ? (
            <p>No distributions found.</p>
          ) : (
            <table className="distributions-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {entitlements.map((entitlement) => (
                  <tr key={entitlement.id}>
                    <td>
                      {entitlement.distribution?.recordDate
                        ? new Date(entitlement.distribution.recordDate).toLocaleDateString('en-NZ')
                        : '-'}
                    </td>
                    <td>{formatCurrency(entitlement.amountCents, entitlement.currency)}</td>
                    <td>
                      <span className={`status-badge ${entitlement.status.toLowerCase()}`}>
                        {entitlement.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
};

export default HolderDashboard;

